import moment from 'moment-timezone';
import { RRule } from 'rrule';

/**
 * Remove props not necessary in BD bordercolor, color, dragBgColor, bgColor, raw
 * 
 * @param {Array} events 
 */
const removeCalendarProps = (events) => {
    const eventsForDB = events.map(({ bgColor, borderColor, color, dragBgColor, raw, ...eventAttributes}) => { 

        Object.keys(eventAttributes).forEach(key => (eventAttributes[key] === undefined || eventAttributes[key] === '') && delete eventAttributes[key]);

        return eventAttributes;
    });

    return eventsForDB;
}

/**
 * Function to put a new Event
 * 
 * @param {Array} stateEvents 
 * @param {Object} newEvent 
 * @param {String} sortKey
 */
const putNewEvent = (stateEvents, newEvent, sortKey) => {
    let events = stateEvents.filter(event =>  moment(event.start).format('YYYY-MM') === sortKey);
    events.push(newEvent);
    
    return events;
}

/**
 * Function to put a new Event
 * 
 * @param {Array} stateRecurrentEvents 
 * @param {Object} newRecurrentEvent 
 * @param {String} sortKey
 */
const putNewRecurrentEvent = (stateRecurrentEvents, newRecurrentEvent) => {
    stateRecurrentEvents.push(newRecurrentEvent);
    return stateRecurrentEvents;
}


/**
 * Function to put the updated event
 * 
 * @param {Array} stateEvents 
 * @param {Object} updatedEvent 
 */
const putUpdatedEvent = (stateEvents, updatedEvent) => { 
    let updatedEvents = stateEvents.map(ev => {
        if (ev.id === updatedEvent.id) {
            return {
                ...updatedEvent,
                read:false
            }
        }

        return ev;
    });

    return updatedEvents;
}

/**
 * Remove event from Events
 * 
 * @param {Array} stateEvents 
 * @param {Object} eventToRemove 
 * @param {String} sortKey 
 */
const removeEvent = (stateEvents, eventToRemove) => {
    let events = stateEvents.filter(ev => ev.id !== eventToRemove.id);

    return events;
}

/**
 * Generate recurrence rrule String for saving event
 * 
 * @param {Object} rRuleOptions
 * @param {Date} rRuleOptions.dtstart 
 * @param {Date} rRuleOptions.until
 * @param {Number} rRuleOptions.freq 
 * @param {Number} rRuleOptions.interval 
 * @param {Number} rRuleOptions.count 
 * @param {Array} rRuleOptions.byweekday 
 */
const generateRRule = (rRuleOptions) => {
    for (const prop in rRuleOptions) {
        if (rRuleOptions[prop] === null || rRuleOptions[prop] === undefined) {
            delete rRuleOptions[prop];
        }
    }

    const rrule = new RRule(rRuleOptions);

    return rrule;
}


/**
 * Format Start and End for All day events for calendar
 * 
 * @param {Array} events 
 */
const formatEventsForCalendarView = (events) => {

    let newAllDayEvents = [];

    let allDayEvents = events.filter(ev => ev.isAllDay === true);
    const noAllDayEvents = events.filter(ev => ev.isAllDay !== true);

    if (allDayEvents.length > 0) {
        newAllDayEvents = allDayEvents.map(ev => {
            return {
                ...ev,
                start: moment(ev.start).format('YYYY-MM-DD HH:mm'),
                end: moment(ev.end).format('YYYY-MM-DD HH:mm')
            }
        })
    }

    let allEvents =  [...newAllDayEvents, ...noAllDayEvents];

    allEvents = allEvents.map((ev) => {
            let startDate = moment(ev.start);
            let endDate = moment(ev.end)
            if (startDate.isDST() && !ev.isAllDay && ev.category === 'time') {
                if (!moment().local().isDST()) {
                    const diff = startDate.diff(endDate, 'minutes');
                    startDate.set('minutes', startDate.get('minutes') + getDSTBias())
                    endDate = startDate.clone().add(Math.abs(diff), 'minutes');

                    ev = {
                        ...ev,
                        start: startDate.format('YYYY-MM-DD HH:mm'),
                        end: endDate.format('YYYY-MM-DD HH:mm')
                    }
                }
            }
            if (startDate.isDST() && ev.isAllDay && ev.category === 'time') {
                if (!moment().local().isDST()) {
                    const diff = startDate.diff(endDate, 'minutes');
                    startDate.set('minutes', startDate.get('minutes') + getDSTBias())
                    endDate = startDate.clone().add(Math.abs(diff), 'minutes');
                    endDate.subtract(1, 'days')

                    ev = {
                        ...ev,
                        start: startDate.format('YYYY-MM-DD HH:mm'),
                        end: endDate.format('YYYY-MM-DD HH:mm')
                    }
                }
            }
            if (!ev.isAllDay && ev.category === 'time' && ev.parentEventId) {
                ev = {
                    ...ev,
                    start: moment(ev.start).toISOString(),
                    end: moment(ev.end).toISOString()
                }
            }

        return {
            ...ev,
            raw: {
                reminderDate: ev.reminderDate,
                reminderString: ev.reminderString,
                check: ev.check,
                deletedDates: ev.deletedDates,
                isLastRepetition: ev.isLastRepetition,
                parentEventId: ev.parentEventId,
                isReminder: ev.isReminder,
                rUntilDate: ev.rUntilDate
            }
        }
    })

    return allEvents;
}

/**
 * Get the dst offset based on the local tz
 */
const getDSTBias = () => {
    var janOffset = moment.tz({ month: 0, day: 1 }, moment.tz.guess()).utcOffset();
    var junOffset = moment.tz({ month: 5, day: 1 }, moment.tz.guess()).utcOffset();
    
    return Math.abs(junOffset - janOffset);
}

/**
 * Generate events array for the calendar view
 * 
 * @param {Array} recurrentEvents 
 */
const generateEventsForCalendarView = (recurrentEvents) => {

    let events = [];

    for (let event of recurrentEvents) {
        event.parentEventId = event.id;
        
        events = events.concat(generateEventsFromRecurrence(event));
    }

    return events
}

const generateEventsFromRecurrence = (event) => {
    const rRule = RRule.fromString(event.recurrenceRule);
    const allDates = rRule.all();
    
    const dates = allDates.map( date => {
        if(!moment(allDates[0]).isDST() && moment(date).isDST()) {
            date.setMinutes(date.getMinutes() - getDSTBias());
        } else if( moment(allDates[0]).isDST() && !moment(date).isDST()) {
            date.setMinutes(date.getMinutes() + getDSTBias());
        }
        return date;
    })

    const events = generateRecurrencyEvents(event, dates);

    return events;
}

const generateRecurrencyEvents = (eventObject, datesArray) => {
    let events = [];
    const eventDuration = getEventDuration(eventObject.start, eventObject.end);

    for (let index = 0; index < datesArray.length; index++) {
        const date = moment(datesArray[index].toISOString()).clone();

        if (eventObject.deletedDates && eventObject.deletedDates.length) {
            if (eventObject.deletedDates.includes(date.format('YYYY-MM-DD'))) {
                continue;
            }
        }

        let newEvent = { ...eventObject };

        if (eventObject.modifiedEvents) {
            let dateToSearch = date.format('YYYY-MM-DD');
            if (eventObject.modifiedEvents.hasOwnProperty(dateToSearch)) {
                newEvent = eventObject.modifiedEvents[dateToSearch];
                newEvent.parentEventId = eventObject.id;
                newEvent.id = newEvent.id.split('recurrence_number_')[0];
                newEvent.id += 'recurrence_number_' + (index +1);
                newEvent.reminderDate = generateReminderDateForRepetition(newEvent.start, newEvent.reminderString);
                newEvent.isLastRepetition = index === (datesArray.length - 1);
                events.push(newEvent);
                continue;
            }
        }

        if (eventObject.checkedTasks) {
            let dateToSearch = date.format('YYYY-MM-DD');
            if (eventObject.checkedTasks.includes(dateToSearch)) {
                newEvent.check = true;
            } else {
                newEvent.check = false;
            }
        }

        if(eventObject.readReminders && eventObject.readReminders.length > 0){
            let dateToSearch = date.format('YYYY-MM-DD');
            if (eventObject.readReminders.includes(dateToSearch)) {
                newEvent.read = true;
            }
        }

        let endDate = moment(newEvent.start).clone();
        endDate.set({year: date.year(), month: date.month(), date: date.date()})
        endDate.add(Math.abs(eventDuration), 'minutes');
        newEvent.start = date.format('YYYY-MM-DD HH:mm');
        newEvent.id += 'recurrence_number_' + (index +1);
        newEvent.end = endDate.format('YYYY-MM-DD HH:mm');
        newEvent.recurrenceRule = eventObject.recurrenceRule
        newEvent.reminderString = eventObject.reminderString;
        if (newEvent.isReminder) {
            newEvent.reminderDate = newEvent.start;
        } else {
            newEvent.reminderDate = generateReminderDateForRepetition(newEvent.start, newEvent.reminderString);
        }
        newEvent.isLastRepetition = index === (datesArray.length - 1);

        events.push(newEvent);
    }

    return events;
}

const generateReminderDateForRepetition = (startDate, reminderString) => {
    if (reminderString) {
        const reminderQuantity = reminderString.split(' ')[0];
        const reminderType = reminderString.split(' ')[1];
        const reminderDate = moment(startDate).local().clone();
        reminderDate.subtract(reminderQuantity, reminderType);

        return reminderDate.toISOString();
    } else {
        return undefined;
    }
}

const getEventDuration = (startDateISO, endDateISO) => {
    const start = moment(startDateISO).clone();
    const end = moment(endDateISO).clone();

    const minutesBetween = end.diff(start, 'minutes');

    return minutesBetween;
    // if (hoursBetween === 24) {
    //     end.set({hours: end.get('h') -1});
    // }

    // let diff = end.diff(start, 'days');

    // return diff + 1;
}

/**
 * Generate a RRule config object from rrule string
 * 
 * @param {string} recurrenceRule 
 */
const generateRecurrencyObjectFromString = (recurrenceRule) => {
    return RRule.fromString(recurrenceRule);
}

/**
 * Create the task buttons
 * 
 * @param {moment.Moment} start 
 */
const createTaskWeek = (start, type, category) => {
    let end = start.clone().set({hour: 23, minute: 59, second: 59});
    let arr = [{
        "calendarId": "addTask",
        "category": category,
        "end": category === 'allday' ? end.toDate() : end.toISOString(),
        "id": "add_task_0",
        "start": start.toISOString(),
        "title": " ",
        "raw": {
            type: "button"
        },
        "bgColor": "none"
    }];

    if (type === 'week'){
        for (let i=1; i<7; i++){
            let task = 
            {
                "calendarId": "addTask",
                "category": category,
                "end": category === 'allday' ? end.add(1, 'day').toDate() : end.add(1, 'day').toISOString(),
                "id": "add_task_" + i,
                "start": start.add(1, 'day').toISOString(),
                "title": " ",
                "raw": {
                    type: "button"
                },
                "bgColor": "none"
            }
            arr.push(task)
        }
    }   
    return arr
}

/**
 * 
 * @param {Array} events 
 * @param {Object} task 
 * @param {String} task.id
 * @param {Object} task.raw
 * @param {Boolean} task.raw.check
 */
const toggleCheckTask = (events, task) => {
    let updatedEvents = [];

    updatedEvents = events.map(ev => {
        if (ev.id === task.id) {
            return {
                ...ev,
                check: !task.raw.check
            }
        }
        return ev;
    });

    return updatedEvents;
}

/**
 * 
 * @param {Array} recurrentEvents 
 * @param {Object} task 
 * @param {String} task.id
 * @param {Object} task.raw
 * @param {Boolean} task.raw.check
 */
const toggleCheckTaskInRecurrent = (recurrentEvents, task) => {
    const taskDate = moment(task.start).format('YYYY-MM-DD');
    let updatedEvents = [];
    task.check = !task.raw.check;
    let parentTask = recurrentEvents.find(ev => ev.id === task.raw.parentEventId);

    let checkedTasks = parentTask.checkedTasks || [];

    if (task.check) {
        checkedTasks.push(taskDate);
    } else {
        if (checkedTasks.includes(taskDate)) {
            checkedTasks = checkedTasks.filter(val => val !== taskDate);
        }
    }

    updatedEvents = recurrentEvents.map(ev => {
        if (ev.id === parentTask.id) {
            return {
                ...parentTask, 
                checkedTasks
            }
        }
        return ev;
    });

    return updatedEvents;
}

/**
 * UPDATE FUNCTIONS
 */

/**
 * Update all Recurrency Events
 * 
 * @param {Array} recurrentEvents 
 * @param {Object} updatedEvent 
 */
const updateAllRecurrencyEvents = (recurrentEvents, parentEventId, updatedEvent, recurrenceChanged = false) => {
    let events = [];
    events = recurrentEvents.map(ev => {
        if (ev.id === parentEventId) {
            if (ev.checkedTasks && ev.checkedTasks.length > 0 && recurrenceChanged) {
                const recurrencyObject = generateRecurrencyObjectFromString(ev.recurrenceRule);
                const untilDate = moment(recurrencyObject.options.until);

                let datesIndexToDelete = [], checkedTasks = ev.checkedTasks;
                ev.checkedTasks.forEach( (date, index) => {
                    if (moment(date).isAfter(untilDate)) {
                        datesIndexToDelete.push(index);
                    }
                })
                for (let index = datesIndexToDelete.length; index >= 0; index--) {
                    checkedTasks.splice(datesIndexToDelete[index], 1);                    
                }
                ev = {
                    ...ev,
                    checkedTasks
                }
            }
            else {
                for (var prop in ev.modifiedEvents) {

                    if (ev.modifiedEvents[prop].id === updatedEvent.id) {
                        let event = ev.modifiedEvents[prop];

                        if (event.calendarId !== updatedEvent.calendarId && event.title === updatedEvent.title &&
                            event.isAllDay === updatedEvent.isAllDay && event.start === updatedEvent.start &&
                            event.end === updatedEvent.end) {
                                if ( (event.reminderString && updatedEvent.reminderString) || event.reminderString === updatedEvent.reminderString){
                                    for (var comp in ev.modifiedEvents) {
                                        ev.modifiedEvents[comp].calendarId = updatedEvent.calendarId;
                                    }
                                    return {
                                        ...ev,
                                        calendarId: updatedEvent.calendarId,
                                    }
                                }
                        }
                    }
                    ev.modifiedEvents[prop].isAllDay = updatedEvent.isAllDay;
                    ev.modifiedEvents[prop].calendarId = updatedEvent.calendarId;
                    ev.modifiedEvents[prop].reminderString = updatedEvent.reminderString;
                    ev.modifiedEvents[prop].title = updatedEvent.title;
                }
            }

            return {
                ...ev,
                title: updatedEvent.title,
                calendarId: updatedEvent.calendarId,
                body: updatedEvent.body,
                location: updatedEvent.location,
                isAllDay: updatedEvent.isAllDay,
                recurrenceRule: recurrenceChanged ? updatedEvent.recurrenceRule : ev.recurrenceRule,
                start: updatedEvent.start,
                reminderString: updatedEvent.reminderString,
                end: updatedEvent.end,
                rUntilDate: updatedEvent.rUntilDate
            }
        }
        return ev;
    })

    return events;
}

/**
 * Update event and future in recurrency (will create)
 * 
 * @param {Array} recurrentEvents 
 * @param {Object} updatedEvent 
 */
const updateEventAndFutureInRecurrency = (recurrentEvents, updatedEvent) => {

}

/**
 * Update only one event in Recurrency (Will create a special event in recurrence)
 * 
 * @param {Array} recurrentEvents 
 * @param {Object} updatedEvent 
 * @param {Object} updatedEvent 
 */
const updateUniqueEventInRecurrency = (recurrentEvents, originRepetitionEvent, updatedEvent) => {
    // Add a special event in the recurrent Event date
    let events = [];
    let isUpdateBefore = false;
    const parentEventId = originRepetitionEvent.raw.parentEventId;
    const repetitionDate = moment(originRepetitionEvent.start.toDate());
    updatedEvent.repetitionOriginalDate = repetitionDate.format('YYYY-MM-DD')
    let parentEvent = recurrentEvents.find(ev => ev.id === parentEventId);
    
    for (var prop in parentEvent.modifiedEvents) {
        if (parentEvent.modifiedEvents[prop].id === updatedEvent.id) {
            updatedEvent.repetitionOriginalDate = prop;
            parentEvent.modifiedEvents[prop] = removeUndefinedPropsInObject(updatedEvent);
            isUpdateBefore = true;
        }
    }

    if (!isUpdateBefore) {
        parentEvent = {
            ...parentEvent,
            modifiedEvents: {
                ...parentEvent.modifiedEvents,
                [repetitionDate.format('YYYY-MM-DD')]: removeUndefinedPropsInObject(updatedEvent)
            }
        }
    }

    events = recurrentEvents.map(ev => {
        if (ev.id === parentEventId) {
            return parentEvent
        }
        return ev;
    })
    
    return events;
}

/***
 * DELETION FUNCTIONS
 */

/**
 * Delete all events in Recurrency
 * 
 * @param {Array} recurrentEvents 
 * @param {Object} eventToDelete 
 */
const removeAllRecurrencyEvents = (recurrentEvents, eventToDelete) => {
    let events = [];
    const eventId = eventToDelete.raw.parentEventId;
    events = recurrentEvents.filter(ev => ev.id !== eventId);

    return events;
}

/**
 * Delete all events after the event
 * 
 * @param {Array} recurrentEvents 
 * @param {Object} eventToDelete
 * @param {String} eventToDelete.id
 * @param {String} eventToDelete.raw.parentEventId 
 */
const removeEventAndFuture = (recurrentEvents, eventToDelete) => {
    let events = [];
    const parentEventId = eventToDelete.raw.parentEventId;

    /**If its the first in recurrence, will delete the complete events */
    if (eventToDelete.id.endsWith('recurrence_number_1')) {
        events = recurrentEvents.filter(ev => ev.id !== parentEventId);
    } else {
        const deletedEventDate = moment(eventToDelete.start.toDate());
        let parentEvent = recurrentEvents.find(ev => ev.id === parentEventId);
        const parentEventRRule = RRule.fromString(parentEvent.recurrenceRule);
        const newUntilDate = deletedEventDate.clone().subtract(1, 'day');

        if (parentEvent.modifiedEvents) {
            Object.keys(parentEvent.modifiedEvents).forEach(
                key => {
                    const keyDate = moment(key);
                    if (keyDate.isSameOrAfter(deletedEventDate)) {
                        delete parentEvent.modifiedEvents[key];
                    }
                }
            )
        }
        const newRRuleObject = {
            dtstart: moment(parentEvent.start).toDate(),
            freq: parentEventRRule.options.freq,
            interval: parentEventRRule.options.interval,
            wkst: parentEventRRule.options.wkst,
            until: newUntilDate.toDate()
        }

        parentEvent = {
            ...parentEvent,
            rUntilDate: "custom",
            recurrenceRule: generateRRule(newRRuleObject).toString()
        }

        events = recurrentEvents.map(ev => {
            if (ev.id === parentEventId) {
                return parentEvent
            }
            return ev;
        })
    }

    return events;
}

/**
 * Delete all events after the event
 * 
 * @param {Array} recurrentEvents 
 * @param {Object} eventToDelete
 * @param {TZDate} eventToDelete.start 
 */
const removeUniqueEventInRecurrence = (recurrentEvents, eventToDelete) => {
    let events = [];
    const eventToRemoveDate = moment(eventToDelete.start.toDate());
    let date = eventToRemoveDate.format('YYYY-MM-DD');
    const parentEventId = eventToDelete.raw.parentEventId;
    let parentEvent = recurrentEvents.find(ev => ev.id === parentEventId);
    let deletedDates = parentEvent.deletedDates || [];
    
    if (parentEvent.modifiedEvents){
        for (let prop in parentEvent.modifiedEvents) {
            if (parentEvent.modifiedEvents[prop].id === eventToDelete.id) {
                date = parentEvent.modifiedEvents[prop].repetitionOriginalDate;
                delete parentEvent.modifiedEvents[prop];
            }
        }
    }

    deletedDates.push(date);
    parentEvent = {
        ...parentEvent,
        deletedDates
    }

    events = recurrentEvents.map(ev => {
        if (ev.id === parentEventId) {
            return parentEvent
        }
        return ev;
    });

    return events;
}

/**
 * Remove emtpy or null properties from object
 * 
 * @param {Object} object 
 */
const removeUndefinedPropsInObject = (object) => {
    Object.keys(object).forEach(key => (object[key] === undefined || object[key] === '') && delete object[key]);

    return object;
}

const markRecurrenceAsSeen = (recurrentEvents, updatedEvent) => {
    const eventDate = moment(updatedEvent.start).format('YYYY-MM-DD');
    let updatedEvents = [];
    let parentEvent = recurrentEvents.find(ev => ev.id === updatedEvent.parentEventId);

    let readReminders = parentEvent.readReminders || [];
    
    readReminders.push(eventDate);

    updatedEvents = recurrentEvents.map(ev => {
        if (ev.id === parentEvent.id) {
            return {
                ...parentEvent, 
                readReminders
            }
        }
        return ev;
    });

    return updatedEvents;
}

const markSimpleAsSeen = (allEvents, updatedEvent) => {
    let events = [];
     events = allEvents.map(ev => {
            if (ev.id === updatedEvent.id) {
                return {
                    ...ev,
                    read: true
                 }
            }
            return ev;
        })
    return events;
}

/**
 * Filter events by sortKey
 * 
 * @param {Array} events 
 * @param {String} sortKey 
 */
const filterEventsBySortKey = (events, sortKey) => {
    if (sortKey === 'recurrentEvents') {
        return events;
    }
    let filteredEvents = events.filter(ev => {
        if (ev.sortKey) {
            return ev.sortKey === sortKey
        } else {
            let evSortKey = moment(ev.start).format('YYYY-MM');
            return evSortKey === sortKey;
        }
    });

    return filteredEvents
}

/**
 * Check, event to delete is a reminder
 * 
 * @param {Array} event
 */
const isReminder = (events, event, isRecurrence = false) => {
    let isReminder = {
        boolean: false,
        reminderScheduleId: ''
    }

    events.forEach(ev => {
        if (!isRecurrence){
            if (event.id === ev.id){
                if (ev.isReminder || (ev.reminderString && ev.reminderScheduleId)){
                    isReminder = {
                        boolean: true,
                        reminderScheduleId: ev.reminderScheduleId
                    }
                }
            }
        }
        else {
            if (event.raw.parentEventId === ev.id){
                if (ev.isReminder || (ev.reminderString && ev.reminderScheduleId)){
                    isReminder = {
                        boolean: true,
                        reminderScheduleId: ev.reminderScheduleId
                    }
                }
            }
        }

    });
    return isReminder
}

export const calendarFunctions = {
    isReminder,
    generateEventsFromRecurrence,
    createTaskWeek,
    putNewEvent,
    putNewRecurrentEvent,
    putUpdatedEvent,
    removeEvent,
    generateEventsForCalendarView,
    formatEventsForCalendarView,
    removeCalendarProps,
    generateRRule,
    generateRecurrencyObjectFromString,
    updateAllRecurrencyEvents,
    updateUniqueEventInRecurrency,
    updateEventAndFutureInRecurrency,
    removeAllRecurrencyEvents,
    removeUniqueEventInRecurrence,
    removeEventAndFuture,
    toggleCheckTask,
    toggleCheckTaskInRecurrent,
    markSimpleAsSeen,
    markRecurrenceAsSeen,
    filterEventsBySortKey
}